// VoyageurInsert.tsx
import React, { useEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Fade,
  Chip,
  InputAdornment,
  Paper,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BadgeIcon from '@mui/icons-material/Badge';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { useSearchParams } from 'react-router-dom';
import creationVoyageur from '../creationVoyageurService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    },
  },
}));

const StyledSelect = styled(Select<string>)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  },
})) as unknown as typeof Select<string>;

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(2),
  borderStyle: 'dashed',
  borderWidth: 2,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderStyle: 'solid',
    backgroundColor: theme.palette.action.hover,
  },
})) as typeof Button;

const PreviewContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  position: 'relative',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface DocumentOrPhotoPickerProps {
  documentType: string;
  onDocumentSelected: (uri: string) => void;
  modeOptions: string[];
  icon: React.ReactNode;
}

const DocumentOrPhotoPicker: React.FC<DocumentOrPhotoPickerProps> = ({
  documentType,
  onDocumentSelected,
  modeOptions,
  icon,
}) => {
  const { t } = useTranslation('voyageur');
  const [preview, setPreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const acceptedTypes = modeOptions.includes('selectPdf') ? 'image/*,application/pdf' : 'image/*';

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPreview(fileURL);
      setFileType(file.type);
      setFileName(file.name);
      onDocumentSelected(fileURL);
    }
  };

  return (
    <Box>
      <UploadButton
        variant="outlined"
        component="label"
        fullWidth
        startIcon={<CloudUploadIcon />}
        size="large"
      >
        {icon}
        <Box ml={1}>
          {preview ? t('documents.modify', { document: documentType }) : t('documents.upload', { document: documentType })}
        </Box>
        <input type="file" hidden accept={acceptedTypes} onChange={handleFileChange} />
      </UploadButton>

      {preview && (
        <Fade in={true}>
          <PreviewContainer>
            {fileName && (
              <Box p={2} bgcolor="background.default">
                <Chip
                  icon={fileType === 'application/pdf' ? <PictureAsPdfIcon /> : <ImageIcon />}
                  label={fileName}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
            {fileType === 'application/pdf' ? (
              <Box sx={{ width: '100%', height: 400 }}>
                <iframe
                  src={preview}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="aperçu pdf"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  backgroundImage: `url(${preview})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: 'grey.100',
                }}
              />
            )}
          </PreviewContainer>
        </Fade>
      )}
    </Box>
  );
};

const VoyageurInsert: React.FC = () => {
  const { t } = useTranslation('voyageur');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const [nom, setNom] = useState<string>('');
  const [prenom, setPrenom] = useState<string>('');
  const [selectedPhotoUri, setSelectedPhotoUri] = useState<string | null>(null);
  const [selectedPasseportUri, setSelectedPasseportUri] = useState<string | null>(null);
  const [adresse, setAdresse] = useState<string>('');
  const [codePostal, setCodePostal] = useState<string>('');
  const [ville, setVille] = useState<string>('');
  const [telephone, setTelephone] = useState<string>('');
  const [profession, setProfession] = useState<string>('');
  const [etatCivil, setEtatCivil] = useState<string>('');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    checkFormValidity();
  }, [
    nom,
    prenom,
    selectedPhotoUri,
    selectedPasseportUri,
    adresse,
    codePostal,
    ville,
    telephone,
    profession,
    etatCivil,
  ]);

  const checkFormValidity = () => {
    const isNomValid = nom.trim() !== '';
    const isPrenomValid = prenom.trim() !== '';
    const isPhotoUriPresent = selectedPhotoUri !== null;
    const isPasseportUriPresent = selectedPasseportUri !== null;
    const isTelephoneValid = telephone.trim() !== '';
    const isProfessionValid = profession.trim() !== '';
    const isEtatCivilValid = etatCivil.trim() !== '';
    const isAdresseValid = adresse.trim() !== '';
    const isCodePostalValid = codePostal.trim() !== '';
    const isVilleValid = ville.trim() !== '';

    setIsFormValid(
      isNomValid &&
        isPrenomValid &&
        isPhotoUriPresent &&
        isPasseportUriPresent &&
        isAdresseValid &&
        isCodePostalValid &&
        isVilleValid &&
        isTelephoneValid &&
        isProfessionValid &&
        isEtatCivilValid
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        await creationVoyageur({
          nom,
          prenom,
          idUtilisateur: String(user.id_utilisateur),
          photoUri: selectedPhotoUri,
          passeportUri: selectedPasseportUri,
          adresse,
          codePostal,
          ville,
          telephone,
          profession,
          etatCivil,
        });
        setIsLoading(false);
        // Rediriger vers le formulaire d'origine si returnUrl est présent
        if (returnUrl) {
          navigate(`${returnUrl}?fromCreation=true`);
        } else {
          navigate('/retour', { state: { tabValue: 2 } });
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setIsLoading(false);
    }
  };

  const handleTelephoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filteredText = e.target.value.replace(/[^0-9]/g, '');
    setTelephone(filteredText);
  };

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
            color: 'white',
          }}
        >
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box ml={2}>
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '2rem' } }}>
                {t('insert.title')}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                {t('insert.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" size={60} />
        </Backdrop>

        <Grid container spacing={3}>
          {/* Informations personnelles */}
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PersonIcon color="primary" />
                  {t('sections.personalInfo')}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label={t('fields.lastName')}
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label={t('fields.firstName')}
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="etat-civil-label">{t('fields.etatCivil')}</InputLabel>
                      <StyledSelect
                        labelId="etat-civil-label"
                        value={etatCivil}
                        label={t('fields.etatCivil')}
                        onChange={(e) => setEtatCivil(e.target.value as string)}
                        startAdornment={
                          <InputAdornment position="start">
                            <FavoriteIcon fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="célibataire">{t('etatCivilOptions.single')}</MenuItem>
                        <MenuItem value="marié">{t('etatCivilOptions.married')}</MenuItem>
                        <MenuItem value="divorcé">{t('etatCivilOptions.divorced')}</MenuItem>
                        <MenuItem value="veuf">{t('etatCivilOptions.widowed')}</MenuItem>
                      </StyledSelect>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Contact */}
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PhoneIcon color="primary" />
                  {t('sections.contactProfession')}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label={t('fields.phone')}
                      value={telephone}
                      onChange={handleTelephoneChange}
                      variant="outlined"
                      type="tel"  // Ajouter cette ligne
                      inputProps={{ 
                        inputMode: 'numeric',
                        pattern: '[0-9]*'  // Ajouter pour iOS
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label={t('fields.profession')}
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Documents et adresse */}
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PhotoCameraIcon color="primary" />
                  {t('sections.documents')}
                </SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DocumentOrPhotoPicker
                      documentType={t('documents.photo')}
                      onDocumentSelected={(uri: string) => setSelectedPhotoUri(uri)}
                      modeOptions={['photo', 'selectPhoto']}
                      icon={<PhotoCameraIcon />}
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                        {t('documents.photoExample')}
                      </Typography>
                      <Box
                        component="img"
                        src="/photoexemple.jpg"
                        alt={t('documents.photoExample')}
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          height: 'auto',
                          borderRadius: 1,
                          boxShadow: 2,
                          display: 'block',
                          mx: 'auto'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        {t('documents.photoExampleCaption')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <DocumentOrPhotoPicker
                      documentType={t('documents.passport')}
                      onDocumentSelected={(uri: string) => setSelectedPasseportUri(uri)}
                      modeOptions={['photo', 'selectPhoto', 'selectPdf']}
                      icon={<BadgeIcon />}
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                        {t('documents.passportExample')}
                      </Typography>
                      <Box
                        component="img"
                        src="/paxexemple.jpg"
                        alt={t('documents.passportExample')}
                        sx={{
                          width: '100%',
                          maxWidth: 300,
                          height: 'auto',
                          borderRadius: 1,
                          boxShadow: 2,
                          display: 'block',
                          mx: 'auto'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        {t('documents.passportExampleCaption')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Adresse */}
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <HomeIcon color="primary" />
                  {t('sections.address')}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      required
                      label={t('fields.address')}
                      value={adresse}
                      onChange={(e) => setAdresse(e.target.value)}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      required
                      label={t('fields.postalCode')}
                      value={codePostal}
                      onChange={(e) => setCodePostal(e.target.value)}
                      variant="outlined"
                      type="tel"  // Ajouter cette ligne
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*'  // Ajouter pour iOS
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      required
                      label={t('fields.city')}
                      value={ville}
                      onChange={(e) => setVille(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Bouton de soumission */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                disabled={!isFormValid}
                onClick={handleSubmit}
                startIcon={<CheckCircleIcon />}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  px: { xs: 4, sm: 6 },
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  textTransform: 'none',
                  fontWeight: 600,
                  background: isFormValid
                    ? 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)'
                    : undefined,
                  boxShadow: isFormValid ? '0 4px 14px rgba(46,125,50,0.3)' : undefined,
                  '&:hover': {
                    background: isFormValid
                      ? 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)'
                      : undefined,
                    boxShadow: isFormValid ? '0 6px 20px rgba(46,125,50,0.4)' : undefined,
                  },
                }}
              >
                {t('insert.submit')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VoyageurInsert;